import { trace } from './services/analyticsService'

// ---------------------------------------------------------------------------
// Iframe-in-webview bridge setup
// When the app loads inside an iframe in a VSCode/Cursor webview,
// the outer webview acts as a message relay to the extension host.
// All API calls stay same-origin (inside the iframe) so cookies work.
// Bridge calls go through parent.postMessage → webview relay → extension.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined' && window.self !== window.top) {
  const params = new URLSearchParams(window.location.search)
  const pluginType = params.get('plugin')

  if (pluginType && !window.eclipseReady) {
    const pending = new Map<
      string,
      { resolve: (v: string) => void; timeout: ReturnType<typeof setTimeout> }
    >()
    let counter = 0
    const TIMEOUT_MS = 60000

    // The bridge's callEclipse path calls eclipseCall(ns, op, JSON.stringify(params)).
    // The thin-shell-manager expects params as an object, not a JSON string,
    // so we parse it back before relaying to the parent webview.
    window.eclipseCall = ((ns: string, op: string, rawParams: unknown): unknown => {
      let params = rawParams
      if (typeof rawParams === 'string') {
        try {
          params = JSON.parse(rawParams)
        } catch {
          /* keep as-is */
        }
      }
      const id = 'bridge_' + Date.now() + '_' + counter++

      try {
        window.parent.postMessage(
          { type: 'bridge-call', id, method: ns, params: [op, params] },
          '*'
        )
      } catch {
        return Promise.resolve(
          JSON.stringify({ success: false, error: 'Parent frame unavailable' })
        )
      }

      return new Promise<string>(resolve => {
        const timeout = setTimeout(() => {
          pending.delete(id)
          resolve(JSON.stringify({ success: false, error: 'Request timed out' }))
        }, TIMEOUT_MS)
        pending.set(id, { resolve, timeout })
      })
    }) as Window['eclipseCall']

    window.addEventListener('message', event => {
      const message = event.data
      if (!message || !message.type) return

      if (message.type === 'bridge-response') {
        const entry = pending.get(message.id)
        if (!entry) return
        clearTimeout(entry.timeout)
        pending.delete(message.id)
        entry.resolve(
          typeof message.payload === 'string' ? message.payload : JSON.stringify(message.payload)
        )
      } else if (message.type === 'bridge-event') {
        if (typeof window.eclipseBridgeMessage === 'function') {
          window.eclipseBridgeMessage(message.payload)
        }
      } else if (message.type === 'bridge-init') {
        const payload = (message.payload || {}) as Record<string, unknown>
        if (payload.env) window.CURIETECH_ENV = payload.env as string
        if (payload.pluginType) window.CURIETECH_PLUGIN_TYPE = payload.pluginType as string
        if (payload.workspacePaths)
          window.CURIETECH_WORKSPACE_PATHS = payload.workspacePaths as string[]
        if (payload.baseUrl) {
          const baseUrl = payload.baseUrl as string
          window.CURIETECH_SERVER_BASE_URL = baseUrl

          // Keep normal API calls same-origin in local plugin dev (localhost:5173)
          // so they use the dev-server proxy and avoid credentialed CORS.
          try {
            const baseOrigin = new URL(baseUrl).origin
            if (window.location.origin === baseOrigin) {
              window.CURIETECH_BASE_URL = baseUrl
            } else {
              delete window.CURIETECH_BASE_URL
            }
          } catch {
            window.CURIETECH_BASE_URL = baseUrl
          }
        }
      }
    })

    window.eclipseReady = true
    window.CURIETECH_PLUGIN_TYPE = pluginType
  }
}

// React Scan must be imported before React runs
if (process.env.NODE_ENV === 'development') {
  import('react-scan')
    .then(({ scan }) => {
      scan({ enabled: false }) // disable it by default, enable it on demand
    })
    .catch(error => {
      // Silently fail if react-scan is not available
      console.warn('react-scan not available:', error)
    })
}

const consoleError = console.error
console.error = function (message, ...args) {
  if (typeof message === 'string') {
    if (message.includes('validateDOMNesting')) return
    if (message.includes('Element already has context attribute:')) return
    // Suppress React DevTools warning
    if (message.includes('Download the React DevTools')) return
  }
  consoleError.apply(console, [message, ...args])
}

// Also suppress React DevTools warning in console.log
const consoleLog = console.log
console.log = function (message, ...args) {
  if (typeof message === 'string' && message.includes('Download the React DevTools')) {
    return
  }
  consoleLog.apply(console, [message, ...args])
}

document.addEventListener('copy', function (e) {
  const selectedText = window.getSelection()?.toString()
  trace({
    event: 'copy_to_clipboard',
    labels: {
      text: selectedText || 'unknown',
      type: 'copy'
    }
  })
})
